import { loanAPI } from '../services/api';

const READ_STORAGE_KEY = 'genlend_inapp_notifications_read_v1';

export type StudentInAppNotificationTone = 'neutral' | 'warning' | 'danger' | 'success';

export interface StudentInAppNotification {
  id: string;
  kind: 'application_feedback' | 'application_pending' | 'payment_due';
  title: string;
  body: string;
  loanNumber?: string;
  tone: StudentInAppNotificationTone;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  nsfas_statement: 'NSFAS statement',
  bank_statement: 'Bank statement',
  student_card: 'Student card',
  id_document: 'ID document',
  other: 'Document',
};

export function getReadNotificationIds(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

export function markNotificationsRead(ids: string[]): void {
  if (ids.length === 0) return;
  const s = getReadNotificationIds();
  ids.forEach((id) => s.add(id));
  localStorage.setItem(READ_STORAGE_KEY, JSON.stringify(Array.from(s)));
}

/** Stable ids from current loans/payments (no extra API calls) — for badge count */
export function computeNotificationIds(loans: any[], payments: any[]): string[] {
  const ids: string[] = [];
  const feedback = loans.find((l: any) => ['rejected', 'requires_info'].includes(l.status));
  if (feedback) ids.push(`app-feedback-${feedback.id}`);
  const pending = loans.find((l: any) => l.status === 'pending');
  if (pending) ids.push(`app-pending-${pending.id}`);
  (payments || [])
    .filter((p: any) => p.status === 'pending')
    .forEach((p: any) => ids.push(`payment-${p.id}`));
  return ids;
}

export function getUnreadNotificationCount(loans: any[], payments: any[]): number {
  const read = getReadNotificationIds();
  return computeNotificationIds(loans, payments).filter((id) => !read.has(id)).length;
}

/**
 * Full notification list for /dashboard/notifications (includes document detail fetches).
 */
export async function fetchStudentInAppNotifications(): Promise<StudentInAppNotification[]> {
  const res = await loanAPI.getStudentPayments();
  if (!res.success || !res.data) return [];

  const { loans, payments } = res.data;
  const items: StudentInAppNotification[] = [];

  const feedbackLoan = loans.find((l: any) => ['rejected', 'requires_info'].includes(l.status));
  if (feedbackLoan) {
    let body = feedbackLoan.rejectionReason || '';
    try {
      const det = await loanAPI.getApplicationDetails(feedbackLoan.id);
      const docs = (det.data?.documents || []).filter(
        (d: any) => d.verification_status === 'rejected'
      );
      if (docs.length > 0) {
        const lines = docs.map((d: any) => {
          const label = DOC_TYPE_LABELS[d.document_type] || d.document_type;
          return d.rejection_reason ? `${label}: ${d.rejection_reason}` : `${label}: needs to be replaced`;
        });
        body = [body, lines.join('\n')].filter(Boolean).join('\n\n');
      }
    } catch {
      /* optional */
    }
    if (!body.trim()) {
      body =
        feedbackLoan.status === 'requires_info'
          ? 'Please submit a new application with the requested information or documents.'
          : 'You can submit a new application if you wish to try again.';
    }
    items.push({
      id: `app-feedback-${feedbackLoan.id}`,
      kind: 'application_feedback',
      title:
        feedbackLoan.status === 'requires_info'
          ? 'We need more information'
          : 'Application not approved',
      body,
      loanNumber: feedbackLoan.loanNumber,
      tone: feedbackLoan.status === 'requires_info' ? 'warning' : 'danger',
    });
  }

  const pendingLoan = loans.find((l: any) => l.status === 'pending');
  if (pendingLoan) {
    let body = "We're verifying your documents. This usually takes 2-4 hours.";
    try {
      const det = await loanAPI.getApplicationDetails(pendingLoan.id);
      const rejected = (det.data?.documents || []).filter(
        (d: any) => d.verification_status === 'rejected'
      );
      if (rejected.length > 0) {
        const lines = rejected.map((d: any) => {
          const label = DOC_TYPE_LABELS[d.document_type] || d.document_type;
          return d.rejection_reason
            ? `${label}: ${d.rejection_reason}`
            : `${label} needs attention`;
        });
        body = `Some documents need attention before we can approve your loan:\n\n${lines.join('\n')}`;
      }
    } catch {
      /* optional */
    }
    items.push({
      id: `app-pending-${pendingLoan.id}`,
      kind: 'application_pending',
      title: 'Application under review',
      body,
      loanNumber: pendingLoan.loanNumber,
      tone: 'neutral',
    });
  }

  for (const p of payments.filter((x: any) => x.status === 'pending')) {
    const amt = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(
      p.amount
    );
    items.push({
      id: `payment-${p.id}`,
      kind: 'payment_due',
      title: 'Payment due',
      body: `Outstanding payment of ${amt} for loan ${p.loanNumber}.`,
      loanNumber: p.loanNumber,
      tone: 'neutral',
    });
  }

  return items;
}
